import { useSearchParams } from "react-router-dom";
import { useSiteData } from "@/context/SiteDataContext";
import { PackageCard } from "@/components/site/PackageCard";
import { CATEGORIES, DURATIONS, IMG } from "@/lib/constants";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHero } from "@/components/site/PageHero";
import { SITE_URL, Seo, breadcrumb } from "@/components/site/Seo";

const DURATION_LABEL = { "2N/3D": "2 Nights / 3 Days", "3N/4D": "3 Nights / 4 Days", "4N/5D": "4 Nights / 5 Days", "5N/6D": "5 Nights / 6 Days", "6N/7D": "6 Nights / 7 Days" };

const Chip = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`shrink-0 h-10 px-4 rounded-full text-sm font-semibold border transition-colors duration-200 ${active ? "bg-ocean text-white border-ocean" : "bg-white text-slate-700 border-slate-200 hover:border-ocean hover:text-ocean"}`}>
    {children}
  </button>
);

export default function Packages() {
  const { packages, loading } = useSiteData();
  const [params, setParams] = useSearchParams();
  const duration = params.get("duration") || "";
  const category = params.get("category") || "";
  const setFilter = (key, value) => {
    const p = new URLSearchParams(params);
    value ? p.set(key, value) : p.delete(key);
    setParams(p, { replace: true });
  };
  const list = packages.filter((p) => (!duration || p.duration === duration) && (!category || p.category === category || p.alt_categories?.includes(category)));
  const groups = (duration ? [duration] : DURATIONS).map((d) => ({ d, items: list.filter((p) => p.duration === d) })).filter((g) => g.items.length);

  return (
    <div data-testid="packages-page">
      <Seo title="Andaman Tour Packages | 2N/3D to 6N/7D Honeymoon, Family & Luxury | Ocean Wings Travel"
        description="Compare Andaman tour packages from 2N/3D to 6N/7D covering Port Blair, Havelock, Neil Island, Baratang and Ross Island. Budget, Standard, Premium, Luxury, Honeymoon and Family trips."
        path="/packages"
        jsonLd={[breadcrumb([["Home", "/"], ["Tour Packages", "/packages"]]), {
          "@context": "https://schema.org", "@type": "ItemList", name: "Andaman tour packages",
          itemListElement: packages.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.name, url: `${SITE_URL}/packages/${p.slug}` })),
        }]} />
      <PageHero img={IMG.radhanagar} alt="Radhanagar beach" eyebrow="Andaman tour packages" title="Find your perfect island itinerary" testId="packages-hero"
        subtitle="Budget, Standard, Premium, Luxury, Honeymoon and Family packages from 2N/3D to 6N/7D, each with a day-wise itinerary." />
      <section className="sticky top-[72px] z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="container-x py-4 space-y-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <Chip active={!duration} onClick={() => setFilter("duration", "")} testId="filter-duration-all">All durations</Chip>
            {DURATIONS.map((d) => <Chip key={d} active={duration === d} onClick={() => setFilter("duration", d)} testId={`filter-duration-${d}`}>{d}</Chip>)}
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <Chip active={!category} onClick={() => setFilter("category", "")} testId="filter-category-all">All types</Chip>
            {CATEGORIES.map((c) => <Chip key={c} active={category === c} onClick={() => setFilter("category", c)} testId={`filter-category-${c.toLowerCase()}`}>{c}</Chip>)}
          </div>
        </div>
      </section>
      <section className="container-x py-12">
        <p className="text-sm text-slate-500" data-testid="packages-result-count">{loading ? "Loading packages…" : `${list.length} package${list.length === 1 ? "" : "s"} found`}</p>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {loading && [1, 2, 3].map((i) => <Skeleton key={i} className="h-[460px] rounded-2xl" />)}
        </div>
        {groups.map(({ d, items }) => (
          <div key={d} className="mt-4 first:mt-0 pb-10" data-testid={`packages-group-${d}`}>
            <div className="flex items-end justify-between gap-3 border-b border-slate-200 pb-3">
              <h2 className="font-display text-3xl text-navy font-semibold">{DURATION_LABEL[d] || d} <span className="font-mono text-base text-ocean align-middle">{d}</span></h2>
              <span className="text-sm text-slate-500">{items.length} package{items.length === 1 ? "" : "s"}</span>
            </div>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{items.map((p) => <PackageCard key={p.id} pkg={p} />)}</div>
          </div>
        ))}
        {!loading && list.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-600" data-testid="packages-empty">
            No packages match these filters. Try another duration or package type.
          </div>
        )}
      </section>
    </div>
  );
}
